import CustomPaginationComponent from "@/components/common/custom-pagination-component";
import { HasAccess } from "@/components/common/has-access";
import { SearchBar } from "@/components/common/search-bar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useInternalUsers } from "@/features/internal-user/hooks/use-internal-user";
import useInvite from "@/features/internal-user/hooks/use-invite";
import { InternalUserCard } from "@/features/internal-user/ui/internal-user-card";
import { useAuth } from "@clerk/clerk-react";
import { XIcon } from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ContentLayout } from "@/components/common/content-layout";

export default function InternalUser() {
  const [email, setEmail] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get("page") || "0", 10);
  const search = searchParams.get("search") || "";
  const { mutate, isPending } = useInvite();
  const { userId } = useAuth();
  const handleInvite = () => {
    mutate(email);
    setEmail("");
  };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isEmailValid = emailRegex.test(email.trim());
  const isDisabled = !email.trim() || !isEmailValid || isPending;

  const { data, isLoading, isFetching, error } = useInternalUsers({
    page,
    search,
  });

  const internalUsers = data?.internalUsers || [];
  const currentPage = data?.currentPage || 0;
  const lastPage = data ? data.lastPage + 1 : 0;

  return (
    <ContentLayout
      header={
        <>
          <SearchBar
            placeholder="Search users..."
            urlParamStrategy="prefix"
            clearButton={{
              enabled: true,
              position: "inside",
              ariaLabel: "Clear search",
              icon: <XIcon className="w-4 h-4" />
            }}
            fieldSelector={{
              enabled: true,
              fields: [
                { value: "name", label: "Name" },
                { value: "email", label: "Email" },
                { value: "phone", label: "Phone" }
              ],
              allowAll: true
            }}
          />
          <HasAccess permission="invite">
            <div className="flex items-center gap-2">
              <Input
                placeholder="Enter email to invite to OPS CRM"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={!isEmailValid && email ? "border-red-500" : ""}
              />
              {!isEmailValid && email && (
                <p className="text-sm text-red-500">
                  Please enter a valid email address.
                </p>
              )}

              <Button onClick={handleInvite} disabled={isDisabled}>
                {isPending ? "Sending..." : "Send Invite"}
              </Button>
            </div>
          </HasAccess>
        </>
      }
      data={internalUsers}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      errorMessage="Failed to load internal users"
      emptyState={{
        message: "No users found matching your criteria"
      }}
      containerClassName="flex flex-col gap-4 h-full p-6 w-full"
      contentClassName="space-y-6 mt-6"
    >
      {internalUsers.map((internalUser: any) => (
        <InternalUserCard
          key={internalUser.id}
          internalUser={internalUser}
          isCurrentUser={internalUser.clerk_id === userId}
        />
      ))}
      {internalUsers.length > 0 && (
        <CustomPaginationComponent
          currentPage={currentPage}
          lastPage={lastPage}
          onPageChange={(page: number) => {
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set("page", page.toString());
            setSearchParams(newSearchParams);
          }}
        />
      )}
    </ContentLayout>
  );
}
